// =============================================================================
// File: frontend/src/utils/telemetry.js
// #83 Phase C — beta telemetry collector (the "double-check" for silent errors +
// API failures a tester never reported).
//
// HARD GUARANTEES (DNBADA — traced full impact):
//  - NO-OP for non-beta users: nothing buffers unless initBetaTelemetry() ran (only
//    called when user.is_beta) → zero impact on regular users.
//  - NEVER throws / never alters requests: it only OBSERVES after the fact, all in try/catch.
//  - METADATA-ONLY, NO PHI (sanitize AT capture): method + path (query STRIPPED) + status +
//    latency; error name + stack-location + capped message; action name/detail. NEVER bodies
//    or field values. The server re-sanitizes (whitelist) as defense-in-depth.
//  - Per-tester OPT-OUT via localStorage; admin kill-switch is server-side.
// =============================================================================
import { API_BASE } from '../config';

const OPTOUT_KEY = 'healist_beta_telemetry_optout';
const FLUSH_MS = 15000;
const MAX_BUFFER = 40;
const EVENTS_PATH = '/api/beta/events';

let _buffer = [];
let _installed = false;
let _fetchWithAuth = null;
let _screen = '';

function optedOut() {
  try { return localStorage.getItem(OPTOUT_KEY) === 'true'; } catch { return false; }
}

// Per-tester opt-out (design §D / strategic #11). When opted out, push()/init() no-op.
export function isBetaTelemetryOptedOut() { return optedOut(); }
export function setBetaTelemetryOptOut(v) {
  try {
    if (v) { localStorage.setItem(OPTOUT_KEY, 'true'); _buffer = []; }
    else localStorage.removeItem(OPTOUT_KEY);
  } catch { /* no-op */ }
}

function push(type, payload, requestId) {
  if (!_installed || optedOut()) return;   // non-beta / opted-out → no-op (zero impact)
  try {
    _buffer.push({
      type, payload,
      screen: _screen || (typeof window !== 'undefined' ? window.location.pathname : ''),
      request_id: requestId,
    });
    if (_buffer.length >= MAX_BUFFER) flush();
  } catch { /* telemetry must never throw */ }
}

export function setBetaScreen(name) { _screen = name || ''; }

export function recordApi(method, url, status, durationMs, requestId) {
  try {
    if (String(url || '').includes(EVENTS_PATH)) return;   // never record our own ingestion call
    let path = String(url || '');
    try { path = new URL(path, window.location.origin).pathname; } catch { path = path.split('?')[0]; }
    push('api', {
      method: String(method || 'GET').toUpperCase(),
      path, status: Number(status) || 0, duration_ms: Math.round(durationMs) || 0,
    }, requestId);
  } catch { /* no-op */ }
}

export function recordAction(name, detail) {
  push('action', {
    name: String(name || '').slice(0, 60),
    detail: detail ? String(detail).slice(0, 200) : undefined,
  });
}

// React render crash — the global window 'error' listener does NOT fire for these, so the app-level
// ErrorBoundary reports them here (no-op for non-beta / opted-out). Metadata-only, same as onError.
export function recordRenderCrash(error, componentStack) {
  try {
    push('error', {
      name: String((error && error.name) || 'RenderCrash').slice(0, 100),
      message: String((error && error.message) || error || '').slice(0, 200),
      source: String((componentStack || (error && error.stack) || '').split('\n')[1] || '').trim().slice(0, 200),
      fatal: true,
    });
    flush();   // fatal — attempt immediate delivery before a possible reload
  } catch { /* telemetry must never throw */ }
}

function onError(e) {
  try {
    const err = (e && e.error) || e;
    push('error', {
      name: String((err && err.name) || 'Error').slice(0, 100),
      message: String((err && err.message) || (e && e.message) || '').slice(0, 200),
      source: String(e && e.filename ? `${e.filename}:${e.lineno || 0}`
        : ((err && err.stack) || '').split('\n')[1] || '').trim().slice(0, 200),
    });
  } catch { /* no-op */ }
}

function onRejection(e) {
  try {
    const r = e && e.reason;
    push('error', {
      name: 'UnhandledRejection',
      message: String((r && r.message) || r || '').slice(0, 200),
      source: String(((r && r.stack) || '').split('\n')[1] || '').trim().slice(0, 200),
    });
  } catch { /* no-op */ }
}

export async function flush() {
  if (!_buffer.length || !_fetchWithAuth) return;
  const batch = _buffer.splice(0, 50);   // already removed from the buffer — a naive drop loses them
  try {
    const res = await _fetchWithAuth(`${API_BASE}${EVENTS_PATH}`, {
      method: 'POST',
      // strip the internal retry counter — it is bookkeeping, never payload
      body: JSON.stringify({ events: batch.map(({ _retry, ...e }) => e) }),
    });
    // A 401 means the token was mid-rotation, NOT that the events were bad. Dropping here is how we
    // lost the telemetry for the onboarding 500 on 2026-07-30: the flush that carried the record of
    // that crash 401'd six seconds after it, and the batch was discarded — the safety net failed at
    // exactly the moment it mattered. Re-queue ONCE so the next flush (post-refresh) carries them.
    // Strictly bounded: one retry per event, capped at MAX_BUFFER — never a retry flood.
    if (res && res.status === 401) {
      const retryable = batch
        .filter(e => (e._retry || 0) < 1)
        .map(e => ({ ...e, _retry: (e._retry || 0) + 1 }));
      if (retryable.length) _buffer.unshift(...retryable.slice(0, MAX_BUFFER));
      if (_buffer.length > MAX_BUFFER) _buffer.length = MAX_BUFFER;
    }
  } catch { /* transport failure — drop, never retry-flood */ }
}

export function initBetaTelemetry(fetchWithAuth) {
  if (_installed || optedOut() || typeof window === 'undefined') return;
  _installed = true;
  _fetchWithAuth = fetchWithAuth;
  try {
    window.addEventListener('error', onError, true);
    window.addEventListener('unhandledrejection', onRejection);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });
    setInterval(flush, FLUSH_MS);
  } catch { /* no-op */ }
}
